"use client";

import { motion, useInView, type Variants } from "framer-motion";
import { ArrowRight, Clock, Tag } from "lucide-react";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { ProductCard } from "@/components/products/product-card";
import { Button } from "@/components/ui/button";
import type { Product } from "@/lib/shopify/types";

const SECTION_CONTENT = {
	tagline: "Today's Specials",
	title: "Limited Time Offers",
	description:
		"Grab these delicious meals at a special price while stocks last. Don't miss out on these exclusive deals!",
	ctaText: "Shop All Deals",
	productBadge: "Save big!",
} as const;

interface SpecialsSectionProps {
	products: Product[];
}

function CountdownTimer({ deadline }: { deadline: string }) {
	const [timeLeft, setTimeLeft] = useState<{
		days: number;
		hours: number;
		minutes: number;
		seconds: number;
	} | null>(null);

	useEffect(() => {
		const calculateTimeLeft = () => {
			const difference = +new Date(deadline) - Date.now();
			if (difference > 0) {
				setTimeLeft({
					days: Math.floor(difference / (1000 * 60 * 60 * 24)),
					hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
					minutes: Math.floor((difference / 1000 / 60) % 60),
					seconds: Math.floor((difference / 1000) % 60),
				});
			} else {
				setTimeLeft(null);
			}
		};

		calculateTimeLeft();
		const timer = setInterval(calculateTimeLeft, 1000);
		return () => clearInterval(timer);
	}, [deadline]);

	if (!timeLeft) return null;

	return (
		<div className="flex gap-4 mt-8">
			{Object.entries(timeLeft).map(([unit, value]) => (
				<div key={unit} className="flex flex-col items-center">
					<div className="bg-white/10 backdrop-blur rounded-lg p-3 min-w-17.5 text-center border border-white/20">
						<span className="text-2xl md:text-3xl font-bold bg-linear-to-br from-white to-gray-400 bg-clip-text text-transparent tabular-nums">
							{value.toString().padStart(2, "0")}
						</span>
					</div>
					<span className="text-xs uppercase tracking-wider mt-2 text-gray-400 font-medium">
						{unit}
					</span>
				</div>
			))}
		</div>
	);
}

const fadeIn: Variants = {
	hidden: { opacity: 0, y: 30 },
	visible: {
		opacity: 1,
		y: 0,
		transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
	},
};

const staggerContainer: Variants = {
	hidden: { opacity: 0 },
	visible: {
		opacity: 1,
		transition: {
			staggerChildren: 0.15,
			delayChildren: 0.2,
		},
	},
};

export function SpecialsSection({ products }: SpecialsSectionProps) {
	const sectionRef = useRef<HTMLElement>(null);
	const isInView = useInView(sectionRef, { once: true, amount: 0.1 });

	const specialProducts = products.filter((product) => {
		const price = parseFloat(product.priceRange.minVariantPrice.amount);
		const compareAtPrice = product.compareAtPriceRange?.minVariantPrice
			? parseFloat(product.compareAtPriceRange.minVariantPrice.amount)
			: 0;
		return compareAtPrice > price;
	});

	const displayProducts =
		specialProducts.length > 0 ? specialProducts : products.slice(0, 4);

	const now = Date.now();
	const deadline = displayProducts
		.map(
			(p) =>
				p.metafields?.find(
					(m) => m?.namespace === "custom" && m?.key === "offer_deadline",
				)?.value,
		)
		.filter((d): d is string => !!d && new Date(d).getTime() > now)
		.sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0];

	if (displayProducts.length === 0) {
		return null;
	}

	return (
		<section
			ref={sectionRef}
			className="py-20 md:py-32 bg-black dark:bg-white text-white dark:text-black overflow-hidden relative"
		>
			<div className="container mx-auto px-4 relative z-10">
				<motion.div
					initial="hidden"
					animate={isInView ? "visible" : "hidden"}
					variants={fadeIn}
					className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16"
				>
					<div>
						<div className="inline-flex items-center gap-2 text-white/80 dark:text-black/80 font-bold tracking-wider uppercase text-sm mb-3">
							<Tag className="w-4 h-4" /> {SECTION_CONTENT.tagline}
						</div>
						<h2 className="text-4xl md:text-5xl font-black mt-2 text-balance tracking-tight">
							{SECTION_CONTENT.title}
						</h2>
						<p className="text-gray-400 dark:text-gray-600 mt-4 max-w-lg text-lg leading-relaxed">
							{SECTION_CONTENT.description}
						</p>
						{deadline && (
							<div className="mt-8">
								<div className="flex items-center gap-2 text-red-500 font-bold uppercase tracking-widest text-sm mb-2">
									<Clock className="w-4 h-4" /> Offer Ends In:
								</div>
								<CountdownTimer deadline={deadline} />
							</div>
						)}
					</div>
					<Button
						size="lg"
						variant="secondary"
						className="gap-2 whitespace-nowrap font-bold rounded-full px-8 dark:bg-black dark:text-white dark:hover:bg-gray-800"
						asChild
					>
						<Link href="/build-a-box">
							{SECTION_CONTENT.ctaText}
							<ArrowRight className="h-4 w-4" />
						</Link>
					</Button>
				</motion.div>

				<motion.div
					variants={staggerContainer}
					initial="hidden"
					animate={isInView ? "visible" : "hidden"}
					className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8"
				>
					{displayProducts.slice(0, 4).map((product) => (
						<motion.div key={product.id} variants={fadeIn}>
							<ProductCard
								product={product}
								badge={SECTION_CONTENT.productBadge}
								className="h-full border-0 shadow-lg"
							/>
						</motion.div>
					))}
				</motion.div>
			</div>
		</section>
	);
}
